/**
 * useModularSectionExitRegeneration
 *
 * Autism-specific counterpart of useSectionExitRegeneration. Ensures that AI
 * paragraph generation fires when a clinician leaves an Autism module section
 * through any exit path — sidebar navigation, Back/Next buttons, breadcrumb
 * clicks, or browser back.
 *
 * Covers all Autism stages that have AI-generated report paragraphs: History,
 * SSI, Observation, Diagnostic Decision (DD), and Post-Assessment Discussion
 * (PAD). DD and PAD were migrated from hand-rolled unmount nets so they also
 * benefit from the ProformaNavigationGuardContext integration (leaving-section
 * dialog on dirty breadcrumb/top-nav, beforeunload on tab close).
 *
 * Key differences from the ADHD hook:
 *
 *   1. Uses the ModularPatientDataContext (subcollection architecture) instead
 *      of PatientDataContext's monolithic dataModel.
 *
 *   2. Reads `stageSectionsGenerated` from the reportContent subcollection
 *      document rather than from `dataModel.6_manageReports.sections[n]`.
 *
 *   3. Uses Autism-specific report stage names ("Autism History",
 *      "Autism Semi-Structured Interview") so the Cloud Function routes to
 *      the correct Autism processors.
 *
 *   4. Registers/clears dirty state via ProformaNavigationGuardContext so
 *      breadcrumb and top-nav confirm dialogs work identically to ADHD.
 *
 * Consumer pages call markRegenHandled() inside every navigation handler that
 * already triggers generation. If the component unmounts without that call,
 * the cleanup effect fires generation as a safety net.
 */
import { useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import { TriggerGenerateDocumentSection } from '../utils/TriggerGenerateDocumentSection';
import { ProformaNavigationGuardContext } from '../context/ProformaNavigationGuardContext';
import { STAGE_KEY_TO_CYP_REPORT_STAGE } from '../constants/cypAdhdStages';

/**
 * Maps Autism stage keys (used in ModularPatientDataContext / Firestore
 * subcollection paths) to the report stage names expected by the
 * GenerateDraftWithLLM Cloud Function.
 */
const STAGE_KEY_TO_REPORT_STAGE = {
  history: 'Autism History',
  semiStructuredInterview: 'Autism Semi-Structured Interview',
  observation: 'Autism Behavioural Observation',
  diagnosticDecision: 'Autism Diagnostic Decision',
  postAssessmentDiscussion: 'Autism Post-Assessment Discussion',
};

// CYP ADHD uses different stage keys and report stage names. The hook resolves
// the correct map at call time via the module parameter.
//
// Imported rather than declared: the Regenerate button needs the same map, and
// while each path kept its own copy they drifted — see the comment on
// STAGE_KEY_TO_CYP_REPORT_STAGE.
const CYP_ADHD_STAGE_KEY_TO_REPORT_STAGE = STAGE_KEY_TO_CYP_REPORT_STAGE;

/**
 * Maps Autism stage keys to the reportContent subcollection document key
 * where stageSectionsGenerated is stored. For now this is a 1:1 mapping
 * (the reportContent doc key matches the assessmentStages key), but the
 * indirection allows the two to diverge in the future if needed.
 */
const STAGE_KEY_TO_REPORT_CONTENT_KEY = {
  history: 'history',
  semiStructuredInterview: 'semiStructuredInterview',
  observation: 'observation',
  diagnosticDecision: 'diagnosticDecision',
  postAssessmentDiscussion: 'postAssessmentDiscussion',
};

/**
 * @param {Object} options
 * @param {string}   options.patientId       - Firestore patient document ID
 * @param {string}   options.stageKey        - Autism stage key (e.g. 'history',
 *                                             'semiStructuredInterview')
 * @param {string}   options.sectionTitle    - Human-readable section title
 *                                             (e.g. 'Presenting Complaint',
 *                                             'Criteria')
 * @param {boolean}  options.hasChanges      - Whether the clinician has edited
 *                                             data since entering the section
 * @param {boolean}  options.canEdit         - Whether the clinician has write
 *                                             permission for this stage
 * @param {Object}   options.reportContent   - Full reportContent state from
 *                                             ModularPatientDataContext
 * @param {string[]} [options.conditions]    - Optional condition flags
 *                                             (e.g. ['patientIsFemale'])
 * @param {boolean}  [options.shouldRegenerate] - Set to false to skip
 *                                             regeneration (e.g. when a form
 *                                             is incomplete). Defaults to true.
 * @param {boolean}  [options.suppressInitialGeneration] - When true, the
 *                                             unmount net only fires for an
 *                                             ALREADY-generated section (i.e.
 *                                             pure regeneration). It never
 *                                             performs a first-time generation
 *                                             on an unmanaged/side exit. Use
 *                                             this on pages where the initial
 *                                             generation is owned exclusively
 *                                             by a specific forward action
 *                                             (e.g. Criterion Mapping ->
 *                                             Review). Defaults to false, which
 *                                             preserves the "generate on first
 *                                             exit" behaviour for every other
 *                                             consumer.
 *
 * @returns {{ markRegenHandled: () => void, resetRegenHandled: () => void }}
 */
export function useModularSectionExitRegeneration({
  patientId,
  stageKey,
  sectionTitle,
  hasChanges,
  canEdit,
  reportContent,
  conditions,
  shouldRegenerate,
  suppressInitialGeneration = false,
  assessmentModule,
}) {
  const regenerationHandled = useRef(false);

  // Resolve the correct stage-to-report-stage map based on the patient's
  // assessment module. CYP ADHD uses different report stage names to route
  // to the CYP-specific LLM processors in the Cloud Function.
  const stageMap = assessmentModule === 'cypAdhd'
    ? CYP_ADHD_STAGE_KEY_TO_REPORT_STAGE
    : STAGE_KEY_TO_REPORT_STAGE;
  const reportStage = stageMap[stageKey] || '';
  const reportContentKey = STAGE_KEY_TO_REPORT_CONTENT_KEY[stageKey] || stageKey;

  // Determine whether this module has been fully completed at least once.
  // Autism tracks this via the assessmentStages doc's moduleStatus field,
  // but for the navigation guard's purposes we check whether any sections
  // have already been generated — a lighter proxy that avoids reading the
  // assessmentStages doc inside this hook.
  const moduleCompletedOnce = useMemo(() => {
    const stageReport = reportContent?.[reportContentKey];
    const generated = stageReport?.stageSectionsGenerated;
    return Array.isArray(generated) && generated.length > 0;
  }, [reportContent, reportContentKey]);

  // Ref snapshot of the latest prop values so the unmount cleanup always
  // reads current state without needing dependency-array updates.
  const latestRef = useRef({
    patientId, stageKey, sectionTitle, hasChanges, canEdit,
    reportContent, conditions, shouldRegenerate, reportStage,
    reportContentKey, moduleCompletedOnce, suppressInitialGeneration,
  });
  latestRef.current = {
    patientId, stageKey, sectionTitle, hasChanges, canEdit,
    reportContent, conditions, shouldRegenerate, reportStage,
    reportContentKey, moduleCompletedOnce, suppressInitialGeneration,
  };

  // ─── Dirty-state registration with navigation guard context ───────
  const { registerDirty, clearDirty } = useContext(ProformaNavigationGuardContext);

  useEffect(() => {
    if (hasChanges && canEdit) {
      registerDirty(sectionTitle, moduleCompletedOnce, reportStage);
    }
    return () => clearDirty();
  }, [hasChanges, canEdit, sectionTitle, reportStage, moduleCompletedOnce, registerDirty, clearDirty]);

  // ─── Mark regeneration as handled ─────────────────────────────────
  // Called by navigation handlers that already trigger generation, so the
  // unmount cleanup skips a duplicate trigger.
  const markRegenHandled = useCallback(() => {
    regenerationHandled.current = true;
  }, []);

  // ─── Reset for intra-route section switches ───────────────────────
  // When the module container stays mounted across section switches, the
  // cleanup effect never fires. The caller resets the flag after triggering
  // generation so it is ready for the next section.
  const resetRegenHandled = useCallback(() => {
    regenerationHandled.current = false;
  }, []);

  // ─── Unmount cleanup: fire-and-forget regeneration ────────────────
  useEffect(() => {
    return () => {
      if (regenerationHandled.current) return;

      const {
        patientId: pid,
        reportStage: stage,
        sectionTitle: section,
        hasChanges: changed,
        canEdit: editable,
        reportContent: rc,
        reportContentKey: rcKey,
        conditions: conds,
        shouldRegenerate: shouldRegen,
        suppressInitialGeneration: regenOnly,
      } = latestRef.current;

      if (!editable) return;
      if (shouldRegen === false) return;
      // Patient Information has no AI-generated paragraph (no prompt config /
      // SECTION_PARAGRAPHS entry on the backend), so never fire generation for
      // it. Mirrors the ADHD useSectionExitRegeneration guard.
      if (section === 'Patient Information') return;

      // Read stageSectionsGenerated from the reportContent subcollection
      // document to determine whether this section has been generated before.
      const stageReport = rc?.[rcKey];
      const generated = stageReport?.stageSectionsGenerated || [];
      const isGenerated = generated.includes(section);

      // Normal consumers generate on the first exit as well as on later dirty
      // exits (`changed || !isGenerated`). Pages that own initial generation
      // through a dedicated forward action opt out of the first-time branch via
      // `suppressInitialGeneration`, so an unmanaged/side exit can only ever
      // REGENERATE a section that already exists (`changed && isGenerated`) —
      // never draft one for the first time behind the clinician's back.
      const shouldFire = regenOnly
        ? (changed && isGenerated)
        : (changed || !isGenerated);

      if (shouldFire) {
        TriggerGenerateDocumentSection(pid, stage, section, conds || []);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { markRegenHandled, resetRegenHandled };
}
